<template>
  <div
    class="flex flex-col w-[320px] flex-shrink-0 max-h-full rounded-xl bg-n-alpha-2 border border-n-weak"
  >
    <!-- Column Header (drag handle for reordering) -->
    <!-- Duas linhas: o nome em cima e os números embaixo. Numa linha só, o
         aviso de parados empurrava o nome e o total pra quebrar. -->
    <div
      class="flex flex-col gap-1.5 p-3 border-b border-n-weak cursor-grab active:cursor-grabbing column-drag-handle"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="w-3 h-3 rounded-full shrink-0"
          :style="{ backgroundColor: column.color || '#ccc' }"
        ></span>
        <h3
          v-tooltip.top="column.name"
          class="text-sm font-semibold text-n-slate-12 m-0 truncate min-w-0"
        >
          {{ column.name }}
        </h3>
        <button
          v-if="automationForStage"
          v-tooltip.top="automationForStage.name"
          class="p-0 bg-transparent border-none cursor-pointer text-n-amber-11 flex items-center shrink-0"
          @click="goToAutomations"
        >
          <span class="i-lucide-zap w-3.5 h-3.5" />
        </button>
        <span
          class="i-lucide-grip-vertical w-4 h-4 text-n-slate-10 ml-auto shrink-0"
        ></span>
      </div>
      <div class="flex items-center gap-2 whitespace-nowrap">
        <span
          class="bg-n-alpha-3 text-n-slate-11 text-xs px-2 py-0.5 rounded-full font-medium"
        >
          {{ displayCount }}
        </span>
        <span
          v-if="staleCount"
          v-tooltip.top="`Parados há mais de ${staleThresholdLabel}`"
          class="text-xs font-semibold px-2 py-0.5 rounded-full bg-n-amber-4 text-n-amber-12 flex items-center gap-1"
        >
          <span class="i-lucide-alarm-clock w-3 h-3" />
          {{ staleCount }} parado{{ staleCount > 1 ? 's' : '' }}
        </span>
        <span
          v-if="formattedTotalValue"
          class="text-xs font-bold text-n-blue-11 ml-auto shrink-0 bg-n-blue-2 px-2 py-0.5 rounded-full"
        >
          {{ formattedTotalValue }}
        </span>
      </div>
    </div>

    <!-- Draggable Area -->
    <div class="flex-1 overflow-y-auto p-2 min-h-[150px] custom-scrollbar">
      <draggable
        :list="conversations"
        group="kanban"
        item-key="id"
        ghost-class="opacity-50"
        @change="onDragChange"
        class="min-h-full flex flex-col gap-2"
      >
        <template #item="{ element, index }">
          <kanban-card
            v-show="isCardVisible(element, index)"
            :conversation="element"
            :stale-after-hours="staleAfterHours"
            @open-drawer="$emit('open-drawer', element)"
            @open-conversation="$emit('open-conversation', $event)"
          />
        </template>
      </draggable>

      <!-- Load More Button -->
      <button
        v-if="hasMoreCards"
        class="w-full mt-2 px-3 py-2 text-xs font-semibold text-n-slate-11 bg-n-surface-1 hover:bg-n-alpha-2 rounded-lg transition-colors cursor-pointer border border-n-weak"
        @click="showMore"
      >
        Ver mais {{ remainingCount }} card{{ remainingCount > 1 ? 's' : '' }}
      </button>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';
import { staleInfo } from './helpers/staleDeal';

const CARDS_PER_PAGE = 20;

// O erro do axios embrulha a resposta; o 422 do negócio traz `message` com a
// lista de pendências montada pelo Crm::Stages::RequirementChecker.
const serverMessage = error =>
  error?.response?.data?.message || error?.response?.data?.error || null;

export default {
  name: 'KanbanColumn',
  components: {
    draggable,
    KanbanCard,
  },
  props: {
    column: {
      type: Object,
      required: true,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    activeAssigneeId: {
      type: Number,
      default: null,
    },
    activeInboxId: {
      type: Number,
      default: null,
    },
    activeTeamId: {
      type: Number,
      default: null,
    },
    activeStatus: {
      type: String,
      default: 'open',
    },
    allColumnLabels: {
      type: Array,
      default: () => [],
    },
    cards: {
      type: Array,
      default: null,
    },
    // [fromSec, toSec] de criação, ou null pra sem filtro. Só age no modo
    // conversational: em modo deals os cards já chegam filtrados do servidor.
    createdRange: {
      type: Array,
      default: null,
    },
  },
  emits: ['open-drawer', 'open-conversation', 'deal-move-failed'],
  data() {
    return {
      visibleLimit: CARDS_PER_PAGE,
    };
  },
  computed: {
    staleAfterHours() {
      return this.column.stale_after_hours || null;
    },
    // Só o que está carregado na coluna: com paginação ("ver mais"), o número
    // cresce conforme os cards chegam. É contagem de tela, não do servidor.
    staleCount() {
      if (!this.staleAfterHours) return 0;
      return this.conversations.filter(card =>
        staleInfo(card, this.staleAfterHours)
      ).length;
    },
    staleThresholdLabel() {
      const hours = this.staleAfterHours;
      if (hours % 24 === 0) {
        const days = hours / 24;
        return `${days} dia${days > 1 ? 's' : ''}`;
      }
      return `${hours} hora${hours > 1 ? 's' : ''}`;
    },
    // Só em modo deals (cards por prop): a coluna carrega stage_id e o raio
    // aparece quando uma regra ativa observa esta etapa.
    automationForStage() {
      if (!this.cards || !this.column.stage_id) return null;
      const automations =
        this.$store.getters['crmAutomations/getAutomations'] || [];
      return automations.find(
        automation =>
          automation.active &&
          automation.trigger_type === 'entered_stage' &&
          automation.trigger_stage_id === this.column.stage_id
      );
    },
    conversations: {
      get() {
        if (this.cards) return this.cards;

        const all = this.$store.getters['getAllConversations'] || [];
        // Sort by last_activity_at DESC so progressive loading slots newer
        // cards above older ones regardless of which page/status they arrived in.
        // Without this, cards appear in Vuex insertion order, which mixes
        // open/pending/resolved batches and feels out of order.
        return all
          .filter(c => {
            if (!c.labels || !c.labels.includes(this.column.label))
              return false;

            if (this.activeAssigneeId !== null) {
              const assigneeId = c.meta?.assignee?.id || 0;
              if (this.activeAssigneeId === 0 && assigneeId !== 0) return false;
              if (
                this.activeAssigneeId > 0 &&
                assigneeId !== this.activeAssigneeId
              )
                return false;
            }

            if (
              this.activeInboxId !== null &&
              c.inbox_id !== this.activeInboxId
            )
              return false;

            if (this.activeTeamId !== null) {
              const teamId = c.meta?.team?.id || c.team_id;
              if (teamId !== this.activeTeamId) return false;
            }

            if (this.activeStatus !== 'all' && c.status !== this.activeStatus)
              return false;

            // "Período de criação" filtra por quando a conversa NASCEU.
            // last_activity_at aqui só ordena; usar ele no recorte traria
            // negociação antiga que recebeu mensagem ontem.
            if (this.createdRange) {
              const ts = c.created_at || 0;
              const [from, to] = this.createdRange;
              if (ts < from || ts > to) return false;
            }

            return true;
          })
          .sort(
            (a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0)
          );
      },
      set(value) {
        // vuedraggable requires a setter
      },
    },
    filteredConversations() {
      const query = (this.searchQuery || '').trim().toLowerCase();
      if (!query) return this.conversations;

      // Telefone e ID são comparados só por dígitos: o contato vem gravado como
      // "+5543999998888" e o usuário digita "43 99999-8888" ou "#1234".
      const digits = query.replace(/\D/g, '');

      return this.conversations.filter(c => {
        const name = (c.meta?.sender?.name || '').toLowerCase();
        const dealTitle = (c.custom_attributes?.deal_title || '').toLowerCase();
        const email = (c.meta?.sender?.email || '').toLowerCase();
        const phone = (c.meta?.sender?.phone_number || '').replace(/\D/g, '');

        if (name.includes(query)) return true;
        if (dealTitle.includes(query)) return true;
        if (email.includes(query)) return true;
        if (digits && phone.includes(digits)) return true;
        if (digits && String(c.id).includes(digits)) return true;

        return false;
      });
    },
    // Build a Set of visible conversation IDs for O(1) lookups
    visibleIds() {
      const visible = this.filteredConversations.slice(0, this.visibleLimit);
      return new Set(visible.map(c => c.id));
    },
    hasMoreCards() {
      return this.filteredConversations.length > this.visibleLimit;
    },
    remainingCount() {
      return this.filteredConversations.length - this.visibleLimit;
    },
    // Soma dos cards carregados. Só serve como fonte da verdade quando não há
    // agregado do servidor (modo conversational) ou quando a busca da tela
    // recorta a lista no cliente.
    loadedValue() {
      return this.filteredConversations.reduce((acc, c) => {
        const val = parseFloat(c.custom_attributes?.deal_value);
        return !isNaN(val) ? acc + val : acc;
      }, 0);
    },
    // Mesma história da soma: a contagem real da etapa vem do servidor.
    displayCount() {
      const hasSearch = (this.searchQuery || '').trim().length > 0;
      const serverCount = this.column?.count;

      if (!hasSearch && serverCount !== undefined && serverCount !== null) {
        return serverCount;
      }

      return this.filteredConversations.length;
    },
    totalValue() {
      const hasSearch = (this.searchQuery || '').trim().length > 0;
      const serverSum = this.column?.value_sum;

      // Em modo deals o servidor soma TODOS os negócios da etapa no Postgres,
      // já com os filtros da tela. A lista de cards é paginada (200), então
      // somar o que está carregado dava um total menor que o real — era isso
      // que não batia em funil grande. Com busca ativa o recorte é do cliente,
      // e aí a soma tem que acompanhar o que está na tela.
      if (!hasSearch && serverSum !== undefined && serverSum !== null) {
        return parseFloat(serverSum) || 0;
      }

      return this.loadedValue;
    },
    formattedTotalValue() {
      if (this.totalValue > 0) {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(this.totalValue);
      }
      return '';
    },
  },
  watch: {
    searchQuery() {
      // Nova busca volta pro topo: sem isso o "Ver mais" acumulado da busca
      // anterior continua valendo e a contagem visível fica inconsistente.
      this.visibleLimit = CARDS_PER_PAGE;
    },
  },
  methods: {
    goToAutomations() {
      this.$router.push({
        name: 'automation_crm_list',
        params: { accountId: this.$route.params.accountId },
      });
    },
    isCardVisible(element, index) {
      // When searching, use the visibleIds set to check
      // When not searching, use index-based check (faster)
      if (this.searchQuery) {
        return this.visibleIds.has(element.id);
      }
      return index < this.visibleLimit;
    },
    showMore() {
      this.visibleLimit += CARDS_PER_PAGE;
    },
    async onDragChange(event) {
      if (event.added) {
        const conversation = event.added.element;

        // Deals mode: cards come by prop, not by conversation label, so
        // moving one means calling the deal's stage endpoint instead of
        // touching conversation labels (whose id space is unrelated to deals).
        if (this.cards) {
          try {
            await this.$store.dispatch('crmDeals/moveToStage', {
              id: conversation.deal_id,
              stageId: this.column.stage_id,
            });
          } catch (error) {
            // A mensagem do servidor diz o que falta ("Para avançar, faltam:
            // Valor do negócio"). Engoli-la deixaria a pessoa só com o card
            // voltando pro lugar, sem saber por quê.
            // Manda também o negócio: o quadro abre a gaveta dele, que é onde
            // estão os campos que faltam preencher.
            this.$emit('deal-move-failed', {
              reason: serverMessage(error),
              card: conversation,
            });
          }
          return;
        }

        // Start from existing labels
        let newLabels = [...(conversation.labels || [])];

        // Strip out ANY label that belongs to the Kanban board
        // to ensure the card only lives in one column
        newLabels = newLabels.filter(
          label => !this.allColumnLabels.includes(label)
        );

        // Add the new destination column label
        newLabels.push(this.column.label);

        await this.$store.dispatch('conversationLabels/update', {
          conversationId: conversation.id,
          labels: newLabels,
        });
      }
    },
  },
};
</script>
